/** Unpacking sort codes and sort-field columns out of the API payloads, for the datasource loaders. */
import { SortCodeItem } from "../../interfaces/SortCodeMap";
import { SortField } from "../../interfaces/SortField";
import { LineItemSortCodeLabel } from "../../interfaces/EstimateData";
import { handleError } from "../../errors";
import {
  fetchApplicationSortFields,
  fetchApplicationSortFieldsUncached,
  fetchProjectSortFields,
  fetchProjectSortFieldsUncached,
  fetchSortCodesBySortFieldId,
  fetchSortCodesBySortFieldIdUncached,
} from "../../api";
import { MFUFlookupByDepth, RESERVED_COLUMN_NAMES_LOWER, safeSortFieldColumnName } from "../utils/sortCodes";

//SortableItem currently includes products, line items, and markups

interface SortableItem {
  mf: { [key: string]: string };
  uf: { [key: string]: string };
  extras: { [key: string]: string };
  [key: string]: unknown;
}

// --- Sort Code Label (SCLO) utilities ---

export type SortCodeLabelJunctionMap = Map<string, { code: string; description: string }>;

export function buildSortFieldColumnNameMap(usedSortFields: { [key: string]: SortField }): Record<string, string> {
  const map: Record<string, string> = {};
  const usedColumnNames = new Set<string>();
  for (const sortFieldId of Object.keys(usedSortFields)) {
    const name = usedSortFields[sortFieldId]?.name;
    if (name) {
      map[sortFieldId] = safeSortFieldColumnName(name, usedColumnNames);
    }
  }
  return map;
}

function unpackExtras(
  obj: SortableItem,
  usedSortFields: { [key: string]: SortField },
  columnNameMap: Record<string, string>
): void {
  for (const sortFieldId of Object.keys(usedSortFields)) {
    const safeName = columnNameMap[sortFieldId];
    if (!safeName) continue;
    const sortCodeId = obj.extras?.[sortFieldId];
    const sortCode = usedSortFields[sortFieldId]?.sort_codes?.[sortCodeId || ""];
    const code = sortCode?.code || "";
    const desc = sortCode?.description || "";
    obj[`${safeName}_code`] = code;
    obj[`${safeName}_desc`] = desc;
    obj[safeName] = `${code}${code && desc ? " - " : ""}${desc}`;
  }
}

function unpackMFUFcodes(obj: SortableItem, lookup: MFUFlookupByDepth, prefix: "mf" | "uf"): void {
  let max = 0;

  // Look for mfuf keys at top level (mf1, mf2, etc.) and within uf & mf keys
  const nested = (obj[prefix] as Record<string, string>) || {};
  for (const objKeys of [obj, nested]) {
    for (const k of Object.keys(objKeys)) {
      if (k.startsWith(prefix)) {
        const num = parseInt(k.slice(prefix.length));
        if (!Number.isNaN(num) && num > max) {
          max = num;
        }
      }
    }
  }

  // Populate levels up to max
  for (let lvl = 1; lvl <= max; lvl++) {
    const key = `${prefix}${lvl}`;
    const value = (obj[key] as string) || (nested[key] as string) || "";

    obj[`${key}_code`] = value;
    const desc = lookup[lvl]?.[value] || "";
    obj[`${key}_desc`] = desc;
    obj[key] = value ? (desc ? `${value} - ${desc}` : value) : "";
  }
}

export function unpackAllSortCodes(
  obj: SortableItem,
  mfLookup: MFUFlookupByDepth,
  ufLookup: MFUFlookupByDepth,
  usedSortFields: { [key: string]: SortField },
  columnNameMap?: Record<string, string>
): void {
  unpackMFUFcodes(obj, mfLookup, "mf");
  unpackMFUFcodes(obj, ufLookup, "uf");
  const map = columnNameMap ?? buildSortFieldColumnNameMap(usedSortFields);
  unpackExtras(obj, usedSortFields, map);
}

export const createSortCodeLookup = (sortCodes: SortCodeItem[]): MFUFlookupByDepth => {
  const lookup: MFUFlookupByDepth = {};

  const processItems = (items: SortCodeItem[], currentDepth: number = 1): void => {
    for (const item of items) {
      if (item.code && item.description) {
        if (!lookup[currentDepth]) {
          lookup[currentDepth] = {};
        }
        lookup[currentDepth][item.code] = item.description;
      }

      // Process children if they exist
      const children = item.children || item.value;
      if (children && Array.isArray(children)) {
        processItems(children, currentDepth + 1);
      }
    }
  };

  processItems(sortCodes);
  return lookup;
};

/**
 * Flattens a hierarchical sort code structure into a flat table
 * Only leaf nodes (nodes without children) are included as rows
 * Each row contains all ancestor codes/descriptions filled in
 */

export const flattenSortCodes = (sortCodes: SortCodeItem[], prefix: "mf" | "uf"): any[] => {
  const result: any[] = [];

  const traverse = (items: SortCodeItem[], ancestors: any[] = [], depth: number = 1): void => {
    for (const item of items) {
      // Check if this node has children
      const children = item.children || item.value;
      const hasChildren = children && Array.isArray(children) && children.length > 0;

      // Only create a row if this is a leaf node (no children)
      if (!hasChildren) {
        const row: any = {};

        // Add all ancestor data
        ancestors.forEach((ancestor, index) => {
          const level = index + 1;
          row[`${prefix}${level}`] = ancestor.code || "";
          row[`${prefix}${level}_desc`] = ancestor.description || "";
        });

        // Add current node data (the leaf)
        row[`${prefix}${depth}`] = item.code || "";
        row[`${prefix}${depth}_desc`] = item.description || "";

        result.push(row);
      } else {
        // If has children, traverse them
        traverse(children, [...ancestors, item], depth + 1);
      }
    }
  };

  traverse(sortCodes);
  return result;
};

export const getSortFieldsWithCodes = async (
  sortFieldIds: string[],
  projectId?: string,
  { fresh = false }: { fresh?: boolean } = {}
): Promise<{ [key: string]: SortField }> => {
  const readApplicationSortFields = fresh ? fetchApplicationSortFieldsUncached : fetchApplicationSortFields;
  const readProjectSortFields = fresh ? fetchProjectSortFieldsUncached : fetchProjectSortFields;
  const readSortCodes = fresh ? fetchSortCodesBySortFieldIdUncached : fetchSortCodesBySortFieldId;
  try {
    const applicationSortFieldsResult = await readApplicationSortFields();
    if (!applicationSortFieldsResult.success) {
      console.error("Error fetching application sort fields:", applicationSortFieldsResult.error);
      throw applicationSortFieldsResult.error;
    }
    const applicationSortFields = applicationSortFieldsResult.data || [];

    let projectSortFields: SortField[] = [];
    if (projectId) {
      const projectSortFieldsResult = await readProjectSortFields(projectId);
      if (!projectSortFieldsResult.success) {
        console.error("Error fetching project sort fields:", projectSortFieldsResult.error, { projectId });
        throw projectSortFieldsResult.error;
      }
      projectSortFields = projectSortFieldsResult.data || [];
    }

    const combinedSortFields = [...applicationSortFields, ...projectSortFields];

    // Filter to only include sort fields that are in sortFieldIds, aka ones in use
    const sortFields = combinedSortFields.filter((field: SortField) => sortFieldIds.includes(field.id));

    const sortCodeResults = await Promise.all(sortFields.map((sortField) => readSortCodes(sortField.id)));

    const result: { [key: string]: SortField } = {};

    sortFields.forEach((sortField, i) => {
      const sortCodesResult = sortCodeResults[i];
      if (!sortCodesResult.success) {
        console.error("Error fetching sort codes:", sortCodesResult.error, { sortFieldId: sortField.id });
        throw sortCodesResult.error;
      }
      const sortCodesMap = (sortCodesResult.data || []).reduce((acc: { [key: string]: any }, code: any) => {
        acc[code.id] = code;
        return acc;
      }, {});
      result[sortField.id] = { ...sortField, sort_codes: sortCodesMap };
    });

    return result;
  } catch (error) {
    throw handleError(error, { sortFieldIds, context: "getSortFieldsWithCodes" });
  }
};

export function buildSortCodeLabelJunctionMap(labels: LineItemSortCodeLabel[]): {
  junctionMap: SortCodeLabelJunctionMap;
  labelBearingSortFieldIds: Set<string>;
} {
  const junctionMap: SortCodeLabelJunctionMap = new Map();
  const labelBearingSortFieldIds = new Set<string>();

  for (const entry of labels) {
    const code = entry.sort_code_label_code ?? entry.sort_code_label_parent_code ?? "";
    const description = entry.sort_code_label_description ?? "";
    junctionMap.set(`${entry.line_item}::${entry.sort_field}`, { code, description });
    labelBearingSortFieldIds.add(entry.sort_field);
  }

  return { junctionMap, labelBearingSortFieldIds };
}

export function buildLabelSortFieldColumnNameMap(
  sortFieldColumnNameMap: Record<string, string>,
  labelBearingSortFieldIds: Set<string>
): Record<string, string> {
  if (labelBearingSortFieldIds.size === 0) return {};

  // Reconstruct usedColumnNames from the base map so label names don't collide
  const usedColumnNames = new Set<string>();
  for (const baseName of Object.values(sortFieldColumnNameMap)) {
    usedColumnNames.add(baseName.toLowerCase());
    usedColumnNames.add(`${baseName}_code`.toLowerCase());
    usedColumnNames.add(`${baseName}_desc`.toLowerCase());
  }
  RESERVED_COLUMN_NAMES_LOWER.forEach((reserved) => {
    usedColumnNames.add(reserved);
  });

  const map: Record<string, string> = {};
  labelBearingSortFieldIds.forEach((sortFieldId) => {
    const baseSafeName = sortFieldColumnNameMap[sortFieldId];
    if (!baseSafeName) return;
    const candidate = `label_${baseSafeName}`;
    map[sortFieldId] = safeSortFieldColumnName(candidate, usedColumnNames);
  });
  return map;
}

export function unpackLabelColumns(
  obj: SortableItem & { id?: string },
  junctionMap: SortCodeLabelJunctionMap,
  labelColumnNameMap: Record<string, string>,
  baseColumnNameMap: Record<string, string>
): void {
  for (const sortFieldId of Object.keys(labelColumnNameMap)) {
    const labelName = labelColumnNameMap[sortFieldId];
    const baseName = baseColumnNameMap[sortFieldId];
    if (!labelName || !baseName) continue;

    const junction = obj.id ? junctionMap.get(`${obj.id}::${sortFieldId}`) : undefined;

    if (junction) {
      const { code, description } = junction;
      obj[`${labelName}_code`] = code;
      obj[`${labelName}_desc`] = description;
      obj[labelName] = `${code}${code && description ? " - " : ""}${description}`;
    } else {
      // Unlabeled row: fall back to base column values
      obj[`${labelName}_code`] = obj[`${baseName}_code`] || "";
      obj[`${labelName}_desc`] = obj[`${baseName}_desc`] || "";
      obj[labelName] = obj[baseName] || "";
    }
  }
}
